import {pluralize, safelyParsePage} from "@rw/string_utils";
import {Dispatch} from "redux";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {IRouteState} from "../../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../../app/services/IServices";
import {getVoivodeshipLocativeName} from "../../../app/types/locationTypes";
import {carsCommonUrl} from "../../../app/utils/read_environment_variables";
import {CarMakeTypes} from "../../../cars_data/cars_data";
import {updateMetaData} from "../../../meta_data/actions/update_meta_data";
import {getOfferListUrl} from "../url_utils/get_offer_list_url";
import {parseOfferListPath} from "../url_utils/parse_offer_list_path";
import {capitalizeFirstLetter} from "../utils/capitalize_first_letter";
import {formatMakeDisplayName} from "../utils/format_make_display_name";
import {formatModelDisplayName} from "../utils/format_model_display_name";
import {ILocation} from "./fetch_location_by_slug_at_route";

interface IProps {
    offersCount: number;
    currentLocationName: ILocation | null;
    /** Polish city label from get-pl-location when available. */
    cityDisplayName?: string;
    offerType: string;
    page: number;
    type?: CarMakeTypes;
    model?: string;
    tag?: string;
    location?: string;
    subLocation?: string;
}

const pluralOffer = pluralize(["oferta", "oferty", "ofert"]);

/** Place label after "w …" — locative voivodeship (Łódzkim) or Polish city name. */
const formatPlace = (props: IProps): string => {
    const {cityDisplayName, location, subLocation} = props;
    if (subLocation) {
        return cityDisplayName || capitalizeFirstLetter(subLocation);
    }
    if (location) {
        return getVoivodeshipLocativeName(location.toLowerCase()) || capitalizeFirstLetter(location);
    }
    return "";
};

const makeModelLabel = (make: string, model?: string): string => {
    const modelLabel = formatModelDisplayName(model);
    if (make && modelLabel) {
        return `${make} ${modelLabel}`;
    }
    return make || modelLabel;
};

export const getCarListTitle = (props: IProps) => {
    const {type, page, offersCount, model} = props;
    const make = formatMakeDisplayName(type);
    const makeModel = makeModelLabel(make, model);
    const place = formatPlace(props);
    const countSuffix = offersCount > 0 ? ` - ${offersCount} ${pluralOffer(offersCount)}` : "";
    const pageSuffix = page > 1 ? ` - strona ${page}` : "";

    let base: string;
    if (makeModel && place) {
        base = `Samochody osobowe ${makeModel} w ${place} na sprzedaż`;
    } else if (makeModel) {
        base = `Samochody osobowe ${makeModel} na sprzedaż`;
    } else if (place) {
        base = `Samochody osobowe w ${place} na sprzedaż`;
    } else {
        base = "Polska samochody osobowe na sprzedaż";
    }

    return `${base}${countSuffix}${pageSuffix}`;
};

export const getCarListDescription = (props: IProps) => {
    const {type, offersCount, page, model} = props;
    const make = formatMakeDisplayName(type);
    const makeModel = makeModelLabel(make, model);
    const place = formatPlace(props);
    const countPhrase =
        offersCount > 0
            ? `${offersCount} ${pluralOffer(offersCount)}`
            : `aktualne ${pluralOffer(0)}`;

    let subject: string;
    if (makeModel && place) {
        subject = make ? `samochodów ${makeModel} w ${place}` : `samochodów ${makeModel} w ${place}`;
    } else if (makeModel) {
        subject = make ? `samochodów ${makeModel}` : `samochodów ${makeModel}`;
    } else if (place) {
        subject = `samochodów osobowych w ${place}`;
    } else {
        subject = "samochodów osobowych w całej Polsce";
    }

    const pageNote = page > 1 ? ` Strona ${page} wyników.` : "";

    if (offersCount > 0) {
        return `Sprawdź ${countPhrase} ${subject}. Porównaj ceny, roczniki i przebiegi na zakupauto.pl.${pageNote}`;
    }

    return `Przeglądaj ${subject} na zakupauto.pl. Nowe ogłoszenia pojawiają się na bieżąco.${pageNote}`;
};

export const updateCarListMetaData =
    (
        _1: Partial<IServices>,
        route: IRouteState<{
            offerType: string;
            type?: CarMakeTypes;
            tag?: string;
            location?: string;
            subLocation?: string;
            subSubLocation?: string;
        }>
    ) =>
    (dispatch: Dispatch, getState: () => IStore) => {
        const state = getState();
        const page = safelyParsePage(route.query.page);
        const currentLocationName = state.carList.location.location;
        const parsed = parseOfferListPath(route.params);
        const offersCount = state.carList.cars.totalItems || 0;
        const cityDisplayName = state.carList.locationPLName?.cityName || undefined;

        const metaProps: IProps = {
            offersCount,
            currentLocationName,
            cityDisplayName,
            offerType: route.params.offerType,
            page,
            type: (parsed.type || route.params.type) as CarMakeTypes | undefined,
            model: parsed.model,
            tag: route.params.tag,
            location: parsed.location,
            subLocation: parsed.subLocation
        };

        const title = getCarListTitle(metaProps);
        const description = getCarListDescription(metaProps);

        const listPath = getOfferListUrl({
            offerType: route.params.offerType as any,
            type: parsed.type || route.params.type,
            model: parsed.model,
            location: parsed.location,
            subLocation: parsed.subLocation,
            tag: route.params.tag as any
        });
        const siteBase = carsCommonUrl.replace(/\/+$/, "");
        const absoluteListPath = listPath.startsWith("/") ? listPath : `/${listPath}`;
        const canonical = page <= 1 ? `${siteBase}${absoluteListPath}` : `${siteBase}${absoluteListPath}?page=${page}`;

        const robots = (() => {
            const {page: pageParam} = route.query;
            const isPageIndexable = safelyParsePage(pageParam) < 11;

            switch (true) {
                case offersCount > 0 && isPageIndexable:
                    return "index, follow";
                default:
                    return "noindex, follow";
            }
        })();

        dispatch(
            updateMetaData(title, description, route, {
                canonical,
                robots
            })
        );
        return Promise.resolve(true);
    };
